import React, { useEffect, useState } from 'react'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { useDispatch } from 'react-redux'
import { getPaymentGatewayOptions } from '../../features/payment/paymentSlice'
import { routeSourceLabel } from '../../containers/Payments/DailyProcessing/helpers'

// Per-charge gateway pick for the single-payment Process dialog (WS 1241875 consistency
// with the Daily Payment Processing console). Fetches the same options + suggestion the
// console rows show and preselects the suggestion; the operator's pick applies to THIS
// charge only — company/account defaults and first-N settings are never touched.
//
// onChange receives { gateway, processor } ONLY when the operator picks something OTHER than
// the suggestion, and null otherwise. Accepting the suggestion must not travel as an explicit
// override: the payment would then be recorded as an operator decision when the operator
// decided nothing, which is exactly the routing attribution this ticket exists to produce.
// (The backend enforces the same rule, so neither side depends on the other getting it right.)
//
// onLoadingChange reports whether the options are still in flight, so the dialog can keep
// Confirm out of reach until this payment's own options have arrived — otherwise a fast
// operator confirms against whatever the previous payment left behind.
const GatewayPicker = ({ paymentId, onChange, onLoadingChange }) => {
  const dispatch = useDispatch()
  const [options, setOptions] = useState(null)
  const [selectedValue, setSelectedValue] = useState('')
  const [loadError, setLoadError] = useState(false)

  const emit = (value, data) => {
    if (!data || value === (data.recommendedGateway || '')) return onChange?.(null)
    const option = (data.availableGateways || []).find((entry) => entry.value === value)
    if (!option) return onChange?.(null)
    onChange?.({ gateway: option.gateway || option.value, processor: option.processor || null })
  }

  useEffect(() => {
    let alive = true

    setOptions(null)
    setSelectedValue('')
    setLoadError(false)
    onChange?.(null)
    onLoadingChange?.(true)

    const load = async () => {
      const result = await dispatch(getPaymentGatewayOptions(paymentId))
      if (!alive) return

      const data = result?.payload?.data
      if (!data || !Array.isArray(data.availableGateways)) {
        setLoadError(true)
        onChange?.(null)
        onLoadingChange?.(false)
        return
      }

      setOptions(data)
      setSelectedValue(data.recommendedGateway || '')
      onChange?.(null)
      onLoadingChange?.(false)
    }

    load()
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId])

  if (loadError) {
    return (
      <Typography variant="body2" color="text.secondary">
        Could not load gateway options — the payment will process with the default routing.
      </Typography>
    )
  }
  if (!options) {
    return <Typography variant="body2" color="text.secondary">Loading gateway options…</Typography>
  }

  return (
    <div>
      <TextField
        select
        fullWidth
        size="small"
        label="Payment gateway"
        value={selectedValue}
        onChange={(event) => {
          setSelectedValue(event.target.value)
          emit(event.target.value, options)
        }}
      >
        {options.availableGateways.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}{option.recommended ? ' (suggested)' : ''}
          </MenuItem>
        ))}
      </TextField>
      {/* WHERE the suggestion came from (company default / account / first-N) — the same
          routeSource the console rows label, so both surfaces attribute it identically. */}
      {options.recommendedGateway && routeSourceLabel(options.routeSource) && (
        <Typography variant="caption" color="text.secondary" data-testid="gateway-source-chip">
          Suggested by: {routeSourceLabel(options.routeSource)}
        </Typography>
      )}
      {!options.recommendedGateway && (
        <Typography variant="caption" color="warning.main">
          No suggestion for this account — its configured gateway ({options.resolvedGateway || 'unknown'}) is
          unavailable. Pick a gateway for this charge.
        </Typography>
      )}
    </div>
  )
}

export default GatewayPicker
